import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { validateHexString } from '../../utils/validateHexString';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    Grid2,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Fab
} from '@mui/material';
import {
    AccountBalance as ContractIcon,
    Build as BuildIcon,
    Edit as SignatureIcon,
    CheckCircle as CheckIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    AccountTree as WorkflowIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import FunctionBuilder from './FunctionBuilder';
import MultiSigWalletSignatureFlow from './MultiSigWalletSignatureFlow';
import SmartHexDisplay from '../utils/SmartHexDisplay';
import WorkflowShortcuts from '../workflow/WorkflowShortcuts';
import { WorkflowTemplate } from '../../utils/workflowTemplates';
import workflowStorageService, { TemplateData } from '../../services/workflowStorageService';
import { useFloatingConverter } from '../../hooks/useFloatingConverter';
import { useWallet } from '../context/WagmiWalletContext';
import { useContracts } from '../context/ContractContext';
import { useSignatureContext } from '../context/SignatureContext';
import logger from '../../utils/logger';

// Define the CollectedSignature interface
interface CollectedSignature {
    signer: string;
    signature: string;
}

// Define the OperationDetails interface
interface OperationDetails {
    hash: string;
    exists: boolean;
    isPending: boolean;
    isReady: boolean;
    isDone: boolean;
    state: string;
    executionTime: string;
    timeRemaining: string;
}

interface TransactionBuilderProps {
    workflowContext: {
        activeWorkflows: { type: string; key: string; timestamp: number }[];
        registerWorkflow: (type: string) => string;
        completeWorkflow: (key: string) => void;
    };
}

const TransactionBuilder: React.FC<TransactionBuilderProps> = ({ workflowContext }) => {
    const { provider, signer, chainId, setExecutionStatus: setGlobalExecutionStatus } = useWallet();
    const { contracts } = useContracts();
    const { signatures, addSignature } = useSignatureContext();

    const [selectedContract, setSelectedContract] = useState<any>(null);
    const [selectedFunction, setSelectedFunction] = useState<string | null>(null);
    const [functionInputs, setFunctionInputs] = useState<{ [key: string]: string[] }>({});
    const [nestedData, setNestedData] = useState<string | null>(null);
    const [showSignatureFlow, setShowSignatureFlow] = useState(false);
    const [executionStatus, setExecutionStatus] = useState('');
    const [transactionValue, setTransactionValue] = useState<string>('');
    const [operationDetails, setOperationDetails] = useState<OperationDetails | null>(null);
    const [operationParams, setOperationParams] = useState({
        target: "",
        value: "",
        data: "",
        predecessor: "0x0000000000000000000000000000000000000000000000000000000000000000",
        salt: "0x0000000000000000000000000000000000000000000000000000000000000000"
    });
    const [owners, setOwners] = useState<string[]>([]);

    // Workflow integration state
    const [showWorkflowShortcuts, setShowWorkflowShortcuts] = useState(false);

    // Floating converter for hex values
    const { openConverter } = useFloatingConverter();

    // Auto-selection from workflow context
    useEffect(() => {
        try {
            const autoSelection = sessionStorage.getItem('workflowAutoSelection');
            if (autoSelection && contracts.length > 0) {
                const data = JSON.parse(autoSelection);
                logger.log('🔄 Processing workflow auto-selection:', data);

                // Find and select the contract
                const contract = contracts.find(c => c.address === data.contractAddress);
                if (contract) {
                    logger.log('✅ Auto-selecting contract:', contract.name);
                    setSelectedContract(contract);
                    setSelectedFunction(data.functionName);

                    // Auto-populate function inputs based on function type
                    if (data.functionName === 'schedule') {
                        // Handle timelock schedule function (existing logic)
                        const possibleKeys = [
                            'step_step1_function_builder_outputs',
                            'previousStepOutputs',
                            'step_step1_function_builder_outputs'
                        ];

                        let stepOutputs = null;
                        logger.log('🔍 Searching for previous step data in sessionStorage...');

                        const allKeys = Object.keys(sessionStorage);
                        logger.log('📋 All sessionStorage keys:', allKeys);

                        for (const key of possibleKeys) {
                            const previousStepData = sessionStorage.getItem(key);
                            logger.log(`🔍 Checking key "${key}":`, previousStepData ? 'FOUND' : 'NOT FOUND');

                            if (previousStepData) {
                                try {
                                    stepOutputs = JSON.parse(previousStepData);
                                    logger.log(`📥 Successfully parsed step data from ${key}:`, stepOutputs);
                                    break;
                                } catch (error) {
                                    logger.warn(`❌ Failed to parse data from ${key}:`, error);
                                }
                            }
                        }

                        if (stepOutputs) {
                            logger.log('✅ Auto-populating schedule parameters with data:', stepOutputs);

                            const resolvedInputs = data.resolvedInputs || {};
                            logger.log('🔧 Resolved inputs from workflow:', resolvedInputs);

                            const functionData = stepOutputs.functionData || '0x';
                            const isSecureReference = functionData.startsWith('{{') && functionData.endsWith('}}');

                            logger.log('🔐 Function data type:', {
                                data: isSecureReference ? functionData : functionData.substring(0, 20) + '...',
                                isSecureReference
                            });

                            const autoInputs = [
                                stepOutputs.targetContract || '',
                                '0',
                                functionData,
                                '0x0000000000000000000000000000000000000000000000000000000000000000',
                                resolvedInputs.salt || '',
                                resolvedInputs.delay || ''
                            ];

                            setFunctionInputs(prev => ({
                                ...prev,
                                'schedule': autoInputs
                            }));

                            logger.log('🎯 Auto-populated schedule inputs:', {
                                target: autoInputs[0],
                                value: autoInputs[1],
                                data: isSecureReference ? autoInputs[2] : autoInputs[2].substring(0, 20) + '...',
                                predecessor: autoInputs[3].substring(0, 20) + '...',
                                salt: autoInputs[4] ? autoInputs[4].substring(0, 20) + '...' : '',
                                delay: autoInputs[5]
                            });
                        } else {
                            logger.warn('⚠️ No previous step data found for auto-population');
                            logger.log('💡 Available sessionStorage keys:', Object.keys(sessionStorage));
                        }
                    } else if (data.functionName === 'execTransaction' || data.functionName === 'executeTransactionWithChecks') {
                        // Handle MultiSig execution functions
                        logger.log('🔐 Auto-populating MultiSig function:', data.functionName);

                        if (data.prefilledInputs) {
                            logger.log('✅ Using pre-filled inputs from workflow:', data.prefilledInputs);

                            // Map the pre-filled inputs to the MultiSig function parameters
                            const autoInputs = [
                                data.prefilledInputs.to || '',                    // to (address)
                                data.prefilledInputs.value || '0',               // value (uint256)
                                data.prefilledInputs.data || '0x',               // data (bytes)
                                data.prefilledInputs.operation || '0',           // operation (uint8)
                                data.prefilledInputs.safeTxGas || '0',           // safeTxGas (uint256)
                                data.prefilledInputs.baseGas || '0',             // baseGas (uint256)
                                data.prefilledInputs.gasPrice || '0',            // gasPrice (uint256)
                                data.prefilledInputs.gasToken || '0x0000000000000000000000000000000000000000', // gasToken (address)
                                data.prefilledInputs.refundReceiver || '0x0000000000000000000000000000000000000000', // refundReceiver (address)
                                data.prefilledInputs.signatures || '0x'          // signatures (bytes)
                            ];

                            setFunctionInputs(prev => ({
                                ...prev,
                                [data.functionName]: autoInputs
                            }));

                            logger.log('🎯 Auto-populated MultiSig inputs:', {
                                to: autoInputs[0],
                                value: autoInputs[1],
                                data: autoInputs[2].startsWith('{{') ? autoInputs[2] : autoInputs[2].substring(0, 20) + '...',
                                operation: autoInputs[3],
                                safeTxGas: autoInputs[4],
                                baseGas: autoInputs[5],
                                gasPrice: autoInputs[6],
                                gasToken: autoInputs[7].substring(0, 10) + '...',
                                refundReceiver: autoInputs[8].substring(0, 10) + '...',
                                signatures: autoInputs[9]
                            });
                        } else {
                            logger.warn('⚠️ No pre-filled inputs found for MultiSig function');
                        }
                    }

                    // TIMING FIX: Don't clear immediately - let FunctionBuilder read resolvedInputs first
                    // Clear after a short delay to allow FunctionBuilder to process resolvedInputs
                    setTimeout(() => {
                        sessionStorage.removeItem('workflowAutoSelection');
                        logger.log('🧹 Cleared workflow auto-selection data (delayed for FunctionBuilder)');
                    }, 100);
                } else {
                    logger.warn('⚠️ Contract not found for auto-selection:', data.contractAddress);
                }
            }
        } catch (error) {
            logger.error('❌ Error processing workflow auto-selection:', error);
        }
    }, [contracts]); // Depend on contracts being loaded

    // Fetch owners when the selected contract changes
    useEffect(() => {
        const fetchOwners = async () => {
            if (selectedContract && signer) {
                try {
                    const contract = new ethers.Contract(
                        selectedContract.address,
                        selectedContract.abi,
                        signer
                    );
                    if (typeof contract.getOwners === 'function') {
                        const fetchedOwners = await contract.getOwners();
                        setOwners(fetchedOwners);
                        logger.log("Fetched owners:", fetchedOwners);
                    } else {
                        logger.log("Contract does not have getOwners method");
                    }
                } catch (error) {
                    logger.error("Error fetching owners:", error);
                }
            }
        };

        fetchOwners();
    }, [selectedContract, signer]);

    // Function handlers
    const handleFunctionSelection = (funcName: string) => {
        setSelectedFunction(funcName);

        // Show signature flow if execTransaction is selected
        if (funcName === 'execTransaction') {
            setShowSignatureFlow(true);
        } else {
            setShowSignatureFlow(false);
        }
    };

    const handleNestedFunctionBuilt = (data: string, functionName: string, inputs: string[]) => {
        setNestedData(data);
        // Save template logic if needed
        saveTemplate({ contract: selectedContract?.name || "", functionName, inputs });
    };

    const handleInputChange = (functionName: string, value: string, index: number) => {
        setFunctionInputs((prev) => {
            // Get existing array or create new one
            const existingArray = prev[functionName] || [];

            // CRITICAL FIX: Ensure array has enough length to prevent sparse arrays
            const updatedValues = [...existingArray];

            // Pad array with empty strings if needed
            while (updatedValues.length <= index) {
                updatedValues.push("");
            }

            // Now safely set the value at the correct index
            updatedValues[index] = value;

            return { ...prev, [functionName]: updatedValues };
        });
    };

    const saveTemplate = async (template: { contract: string; functionName: string; inputs: string[] }) => {
        try {
            // Create descriptive name with timestamp
            const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_');
            const descriptiveName = `${template.contract}_${template.functionName}_${timestamp}`;

            // Create template data for backend storage
            const templateData: TemplateData = {
                contract: template.contract,
                contractAddress: selectedContract?.address || '',
                functionName: template.functionName,
                functionData: nestedData || '',
                inputs: template.inputs,
                timestamp: Date.now(),
                description: descriptiveName,
                tags: ['function-builder', template.contract.toLowerCase(), template.functionName.toLowerCase()]
            };

            // Try to save to backend first
            const response = await workflowStorageService.storeTemplate(templateData);

            if (response.success) {
                setExecutionStatus(`✅ Template "${descriptiveName}" saved successfully! ID: ${response.id}`);
                logger.log('Template stored with descriptive name:', descriptiveName);
            } else {
                throw new Error(response.error || 'Failed to store template');
            }
        } catch (error) {
            logger.error('Backend template save failed:', error);

            // Fallback to localStorage with timestamp
            try {
                const timestamp = new Date().toISOString().slice(0, 16).replace('T', '_');
                const descriptiveName = `${template.contract}_${template.functionName}_${timestamp}`;

                const fallbackTemplate = {
                    ...template,
                    name: descriptiveName,
                    timestamp: Date.now(),
                    id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                };

                const savedTemplates = JSON.parse(localStorage.getItem('transactionTemplates') || '[]');
                savedTemplates.push(fallbackTemplate);
                localStorage.setItem('transactionTemplates', JSON.stringify(savedTemplates));

                setExecutionStatus(`⚠️ Backend unavailable. Template "${descriptiveName}" saved locally as fallback.`);
            } catch (fallbackError) {
                setExecutionStatus(`❌ Failed to save template: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }
    };

    // Utility function to track transaction chain
    const trackTransactionChain = async (
        transaction: ethers.providers.TransactionResponse,
        setStatus?: (status: string) => void,
        contractsList: { name: string; address: string; abi: any }[] = []
    ) => {
        try {
            // Track transaction status
            const txHash = transaction.hash;
            setStatus?.(`Transaction submitted: ${txHash}`);

            // Wait for transaction to be mined
            const receipt = await transaction.wait(1);

            if (receipt.status === 1) {
                setStatus?.(`Transaction completed successfully. Hash: ${txHash}`);
            } else {
                setStatus?.(`Transaction failed. Hash: ${txHash}`);
            }

            return receipt;
        } catch (error) {
            logger.error("Error tracking transaction:", error);
            setStatus?.(`Error with transaction: ${error instanceof Error ? error.message : String(error)}`);
            throw error;
        }
    };

    // Helper function to check a scheduled operation
    const checkScheduledOperation = async (timelockContract: ethers.Contract, args: { target: string; value: string; data: string; predecessor: string; salt: string }) => {
        try {
            // Extract the parameters from the schedule call
            const { target, value, data, predecessor, salt } = args;

            // Compute the operation hash
            const operationHash = await timelockContract.hashOperation(
                target, value, data, predecessor, salt
            );

            // Check operation status
            const exists = await timelockContract.isOperation(operationHash);
            if (!exists) {
                logger.log("Operation does not exist");
                return null;
            }

            const isPending = await timelockContract.isOperationPending(operationHash);
            const isReady = await timelockContract.isOperationReady(operationHash);
            const isDone = await timelockContract.isOperationDone(operationHash);

            // Get the state as enum (more readable)
            const stateNumber = await timelockContract.getOperationState(operationHash);
            const states = ["Unset", "Waiting", "Ready", "Done"];

            // Get scheduled timestamp
            const timestamp = await timelockContract.getTimestamp(operationHash);
            const currentTime = Math.floor(Date.now() / 1000);
            const timeRemaining = timestamp > currentTime ?
                `${((timestamp - currentTime) / 60).toFixed(1)} minutes` :
                "Ready for execution";

            return {
                hash: operationHash,
                exists,
                isPending,
                isReady,
                isDone,
                state: states[stateNumber],
                executionTime: new Date(timestamp * 1000).toLocaleString(),
                timeRemaining
            };
        } catch (error) {
            logger.error("Error checking operation:", error);
            return null;
        }
    };

    const handleSignaturesCollected = async (newSignatures: CollectedSignature[]) => {
        // Use the array of signatures directly
        try {
            if (!signer) {
                setExecutionStatus("No signer available.");
                return;
            }
            if (!selectedContract || !selectedContract.abi) {
                setExecutionStatus("No contract selected or ABI not loaded.");
                return;
            }
            const filteredABI = selectedContract.abi.filter(
                (item: any) =>
                    !(item.name === 'Paused' && item.inputs.length === 2) &&
                    !(item.name === 'Unpaused' && item.inputs.length === 2)
            );

            const contract = new ethers.Contract(selectedContract.address, filteredABI, signer);

            // Copy the existing execTransaction inputs from the UI,
            // and fill in defaults for any undefined value:
            const finalInputs = [...(functionInputs["execTransaction"] || [])];

            // VALIDATE INPUTS - now with stricter validation that fails on invalid data
            // Validate 'to' address
            if (!finalInputs[0] || !ethers.utils.isAddress(finalInputs[0])) {
                setExecutionStatus("Error: Invalid 'to' address. Please provide a valid Ethereum address.");
                return;
            }

            // Ensure value is a valid numeric string
            if (!finalInputs[1] || !/^[\d.]+$/.test(finalInputs[1])) {
                setExecutionStatus("Error: Invalid value. Please provide a valid number.");
                return;
            }

            // Validate data field (which contains the encoded function call)
            if (finalInputs[2]) {
                // Add 0x prefix if missing
                if (!finalInputs[2].startsWith('0x')) {
                    finalInputs[2] = '0x' + finalInputs[2];
                }

                // Validate hex string (without the 0x prefix)
                const hexData = finalInputs[2].substring(2);
                const hexValidation = validateHexString(hexData, "Transaction data");

                if (!hexValidation.isValid) {
                    setExecutionStatus(`Error: ${hexValidation.message}`);
                    return;
                }
            } else {
                finalInputs[2] = "0x"; // Default to empty data
            }

            // Validate remaining numeric inputs
            for (let i = 3; i <= 6; i++) {
                if (finalInputs[i] && !/^\d+$/.test(finalInputs[i])) {
                    setExecutionStatus(`Error: Invalid numeric parameter at position ${i}. Please provide a valid integer.`);
                    return;
                }
                // Set default value if not provided
                finalInputs[i] = finalInputs[i] || "0";

            }

            // Set default addresses for gasToken and refundReceiver
            finalInputs[7] = finalInputs[7] || ethers.constants.AddressZero;
            finalInputs[8] = finalInputs[8] || ethers.constants.AddressZero;

            // Log validated inputs for debugging
            logger.log("Validated execTransaction inputs:", finalInputs);

            // Sort signatures by signer address (ascending)
            const sortedSignatures: CollectedSignature[] = [...newSignatures].sort((a, b) => {
                const addressA = a.signer.toLowerCase();
                const addressB = b.signer.toLowerCase();
                if (addressA < addressB) return -1;
                if (addressA > addressB) return 1;
                return 0;
            });

            // Concatenate the sorted signatures
            let concatenatedSignatures = "0x";
            for (const sig of sortedSignatures) {
                // Remove '0x' prefix from all signatures
                concatenatedSignatures += sig.signature.slice(2);
            }
            finalInputs[9] = concatenatedSignatures;

            // Retrieve the nonce
            const nonce = await contract.nonce();

            // Compute the transaction hash (the data that was signed by each owner)
            const txHash = await contract.getTransactionHash(
                finalInputs[0],
                finalInputs[1],
                finalInputs[2],
                finalInputs[3],
                finalInputs[4],
                finalInputs[5],
                finalInputs[6],
                finalInputs[7],
                finalInputs[8],
                nonce
            );

            logger.log("Transaction hash:", txHash);
            logger.log("Owners:", owners);
            logger.log("Sorted signers:", sortedSignatures.map(s => s.signer));

            // Off-chain validation: verify that we have enough valid signatures
            try {
                const threshold = await contract.getThreshold();
                logger.log(`Required signatures: ${threshold.toString()}`);
                logger.log(`Collected signatures: ${sortedSignatures.length}`);

                if (sortedSignatures.length < threshold) {
                    throw new Error(`Not enough signatures: ${sortedSignatures.length}/${threshold}`);
                }
            } catch (validationError) {
                logger.error("Signature validation failed:", validationError);
                setExecutionStatus("Signature validation failed. Please check your inputs or try again.");
                return;
            }

            // If validation passes, execute the transaction
            const txResponse = await contract.execTransaction(...finalInputs, {
                value: ethers.utils.parseEther(transactionValue || "0"),
            });

            // Use our enhanced transaction logging utility with status updates
            await trackTransactionChain(txResponse, setExecutionStatus, contracts);
        } catch (txError: any) {
            logger.error("Error executing transaction:", txError);
            setExecutionStatus(`Error executing transaction: ${txError.message || "Check logger for details."}`);
        } finally {
            setShowSignatureFlow(false);
        }
    };

    const handleSignerChanged = (newSigner: ethers.Signer) => {
        logger.log("Signer updated in TransactionBuilder:", newSigner);
        // No need to set signer here as it comes from the context
    };

    // Workflow integration handlers
    const handleStartWorkflow = (template: WorkflowTemplate) => {
        logger.log('🚀 Starting workflow from Transaction Builder:', template.name);

        // Close the shortcuts drawer
        setShowWorkflowShortcuts(false);

        // Navigate to workflows tab and start the selected template
        // This would typically be handled by a parent component or router
        // For now, we'll just log and show a message
        setExecutionStatus(`Starting workflow: ${template.name}. Please navigate to the Workflows tab to continue.`);

        // In a real implementation, this might:
        // 1. Switch to the workflows tab
        // 2. Pre-populate the workflow with current transaction data
        // 3. Start the workflow execution
    };

    const handleCreateWorkflowFromCurrent = async () => {
        logger.log('💾 Creating workflow from current transaction');

        if (!selectedContract || !selectedFunction || !nestedData) {
            setExecutionStatus("Please build a function first before creating a workflow.");
            return;
        }

        try {
            setExecutionStatus("Saving workflow template to secure backend...");

            // Create a template using the backend storage service
            const templateData: TemplateData = {
                contract: selectedContract.name,
                contractAddress: selectedContract.address,
                functionName: selectedFunction,
                functionData: nestedData,
                inputs: functionInputs[selectedFunction] || [],
                timestamp: Date.now(),
                description: `Custom template for ${selectedContract.name}.${selectedFunction}`,
                tags: ['custom', 'transaction-builder', selectedContract.name.toLowerCase()]
            };

            const response = await workflowStorageService.storeTemplate(templateData);

            if (response.success) {
                setExecutionStatus(`✅ Workflow template created successfully! ID: ${response.id}`);
                logger.log('Template stored with ID:', response.id);
            } else {
                throw new Error(response.error || 'Failed to store template');
            }
        } catch (error) {
            logger.error('Failed to store workflow template:', error);

            // Fallback to localStorage if backend fails
            try {
                const fallbackData = {
                    contract: selectedContract.name,
                    contractAddress: selectedContract.address,
                    functionName: selectedFunction,
                    functionData: nestedData,
                    inputs: functionInputs[selectedFunction] || [],
                    timestamp: Date.now()
                };

                const savedWorkflows = JSON.parse(localStorage.getItem('customWorkflows') || '[]');
                savedWorkflows.push(fallbackData);
                localStorage.setItem('customWorkflows', JSON.stringify(savedWorkflows));

                setExecutionStatus(`⚠️ Backend unavailable. Template saved locally as fallback.`);
            } catch (fallbackError) {
                setExecutionStatus(`❌ Failed to save template: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        }

        setShowWorkflowShortcuts(false);
    };

    // Get current context for workflow suggestions
    const getCurrentContext = () => {
        return {
            contract: selectedContract?.name,
            functionName: selectedFunction || undefined,
            functionData: nestedData || undefined,
            description: selectedContract && selectedFunction ?
                `${selectedContract.name}.${selectedFunction}` : undefined
        };
    };

    // Timelock Operation Functions
    const handleCheckOperation = async () => {
        if (!provider || !selectedContract) {
            setExecutionStatus("Provider or contract not initialized.");
            return;
        }

        try {
            // Find the timelock contract in your contracts list
            const timelockContract = contracts.find(c => c.name.toLowerCase().includes("timelock"));

            if (!timelockContract) {
                setExecutionStatus("Timelock contract not found in contracts list.");
                return;
            }

            // Create a contract instance using the found contract's address and ABI
            const timelockInstance = new ethers.Contract(
                timelockContract.address,
                timelockContract.abi,
                provider.getSigner()
            );

            // Use the utility function with your args object
            const details = await checkScheduledOperation(timelockInstance, operationParams);

            if (details !== null) {
                setOperationDetails(details);
                setExecutionStatus(`Operation ${details.exists ? "exists" : "does not exist"}`);
            } else {
                setOperationDetails(null);
                setExecutionStatus("Failed to check operation.");
            }
        } catch (error) {
            logger.error("Error:", error);
            setExecutionStatus("Error checking operation. See logger.");
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Transaction Builder
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {executionStatus && (
                        <CircularProgress size={24} />
                    )}
                    <Button
                        variant="outlined"
                        onClick={handleCheckOperation}
                        disabled={!selectedContract}
                        startIcon={<InfoIcon />}
                    >
                        Check Operation
                    </Button>
                </Box>
            </Box>

            <Grid2 container spacing={3}>
                {/* Contract Selection Card */}
                <Grid2 size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 3, height: 'fit-content' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <ContractIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">
                                Contract Selection
                            </Typography>
                        </Box>

                        {contracts.length > 0 ? (
                            <FormControl fullWidth>
                                <InputLabel>Select Contract</InputLabel>
                                <Select
                                    value={selectedContract?.address || ''}
                                    label="Select Contract"
                                    onChange={(e) => {
                                        const contract = contracts.find(c => c.address === e.target.value);
                                        setSelectedContract(contract || null);
                                    }}
                                >
                                    {contracts.map((contract) => (
                                        <MenuItem key={contract.address} value={contract.address}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                <Typography variant="body1">{contract.name}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {contract.address.substring(0, 10)}...{contract.address.substring(contract.address.length - 8)}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <Alert severity="info">No contracts available</Alert>
                        )}

                        {selectedContract && (
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    label={selectedContract.name}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mb: 1 }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {selectedContract.address}
                                </Typography>
                                {owners.length > 0 && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <strong>Owners:</strong> {owners.length}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Paper>
                </Grid2>

                {/* Function Builder Card */}
                <Grid2 size={{ xs: 12, md: 8 }}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <BuildIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6">
                                Function Builder
                            </Typography>
                        </Box>

                        {selectedContract ? (
                            <FunctionBuilder
                                abi={selectedContract.abi}
                                onFunctionBuilt={(data) => handleNestedFunctionBuilt(
                                    data,
                                    selectedFunction || '',
                                    functionInputs[selectedFunction || ''] || []
                                )}
                                onFunctionSelect={handleFunctionSelection}
                                inputs={functionInputs}
                                onInputChange={handleInputChange}
                                selectedFunction={selectedFunction || undefined}
                                contractAddress={selectedContract.address}
                                isWorkflowStep={!!sessionStorage.getItem('workflowStepData')}
                            />
                        ) : (
                            <Alert severity="info">
                                Please select a contract to build functions
                            </Alert>
                        )}
                    </Paper>
                </Grid2>

                {/* Signature Collection Card */}
                {showSignatureFlow && selectedContract && signer && (
                    <Grid2 size={{ xs: 12 }}>
                        <Paper sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SignatureIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6">
                                    Signature Collection
                                </Typography>
                            </Box>
                            <MultiSigWalletSignatureFlow
                                selectedContract={selectedContract}
                                signer={signer}
                                chainId={chainId.toString()}
                                functionInputs={functionInputs}
                                owners={owners}
                                onSignaturesCollected={handleSignaturesCollected}
                                onSignerChanged={handleSignerChanged}
                            />
                        </Paper>
                    </Grid2>
                )}


                {/* Status Card */}
                {executionStatus && (
                    <Grid2 size={{ xs: 12 }}>
                        <Alert
                            severity={
                                executionStatus.toLowerCase().includes('error') ? 'error' :
                                    executionStatus.toLowerCase().includes('success') ? 'success' : 'info'
                            }
                            icon={
                                executionStatus.toLowerCase().includes('error') ? <ErrorIcon /> :
                                    executionStatus.toLowerCase().includes('success') ? <CheckIcon /> : <InfoIcon />
                            }
                        >
                            <SmartHexDisplay
                                value={executionStatus}
                                label="Transaction Status"
                                showInlineConversion={true}
                                compactConversion={true}
                                onOpenFloatingConverter={openConverter}
                            />
                        </Alert>
                    </Grid2>
                )}

                {/* Operation Details Card */}
                {operationDetails && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Operation Details
                            </Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Operation Hash
                                    </Typography>
                                    <SmartHexDisplay
                                        value={operationDetails.hash}
                                        label="Operation Hash"
                                        showInlineConversion={true}
                                        compactConversion={true}
                                        onOpenFloatingConverter={openConverter}
                                    />
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            State
                                        </Typography>
                                        <Chip
                                            label={operationDetails.state}
                                            color={
                                                operationDetails.state === 'Ready' ? 'success' :
                                                    operationDetails.state === 'Waiting' ? 'warning' : 'default'
                                            }
                                            size="small"
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Time Remaining
                                        </Typography>
                                        <Typography variant="body2">
                                            {operationDetails.timeRemaining}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Execution Time
                                    </Typography>
                                    <Typography variant="body2">
                                        {operationDetails.executionTime}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid2>
                )}
            </Grid2>

            {/* Workflow Shortcuts Floating Action Button */}
            <Fab
                color="primary"
                aria-label="workflow shortcuts"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 1000
                }}
                onClick={() => setShowWorkflowShortcuts(true)}
            >
                <WorkflowIcon />
            </Fab>

            {/* Workflow Shortcuts Drawer */}
            <Drawer
                anchor="right"
                open={showWorkflowShortcuts}
                onClose={() => setShowWorkflowShortcuts(false)}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: 400,
                        maxWidth: '90vw'
                    }
                }}
            >
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">
                        Workflow Shortcuts
                    </Typography>
                    <IconButton onClick={() => setShowWorkflowShortcuts(false)}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider />
                <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
                    <WorkflowShortcuts
                        onStartWorkflow={handleStartWorkflow}
                        onCreateWorkflowFromCurrent={handleCreateWorkflowFromCurrent}
                        currentContext={getCurrentContext()}
                        compact={false}
                    />
                </Box>
            </Drawer>
        </Box>
    );
};

export default TransactionBuilder;
